import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLocation, Link } from "react-router-dom";
import { LayoutDashboard, Building2, CalendarDays, TrendingUp, Sparkles, LogOut, FileText, Users, Settings, Eye, EyeOff, X, Moon, Sun, Receipt } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const navItems = [
  { label: "Dashboard", path: "/", icon: LayoutDashboard, testId: "sidebar-dashboard-link" },
  { label: "Proprietà", path: "/proprieta", icon: Building2, testId: "sidebar-proprieta-link" },
  { label: "Prenotazioni", path: "/prenotazioni", icon: CalendarDays, testId: "sidebar-prenotazioni-link" },
  { label: "Guadagni", path: "/guadagni", icon: TrendingUp, testId: "sidebar-guadagni-link" },
  { label: "Costi", path: "/costi", icon: Receipt, testId: "sidebar-costi-link" },
  { label: "Documenti", path: "/documenti", icon: FileText, testId: "sidebar-documenti-link" },
  { label: "Report AI", path: "/report-ai", icon: Sparkles, testId: "sidebar-report-ai-link" },
  { label: "Proprietari", path: "/proprietari", icon: Users, adminOnly: true, testId: "sidebar-proprietari-link" },
];

export default function AppSidebar({ onNavigate }) {
  const { user, logout, API } = useAuth();
  const { dark, toggleTheme } = useTheme();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const handleClick = () => { if (onNavigate) onNavigate(); };
  const displayName = user?.cognome ? `${user.name} ${user.cognome}` : user?.name;

  const openProfile = () => {
    setProfileForm({
      name: user?.name || "", cognome: user?.cognome || "", email: user?.email || "",
      telefono: user?.telefono || "", codice_fiscale: user?.codice_fiscale || "",
      indirizzo: user?.indirizzo || "", iban: user?.iban || ""
    });
    setNewPassword("");
    setNewUsername("");
    setCurrentPassword("");
    setShowPassword(false);
    setProfileOpen(true);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = { ...profileForm };
      if (newPassword) payload.password = newPassword;
      await axios.put(`${API}/auth/profile`, payload);

      if (newUsername && newUsername !== user.username) {
        if (!currentPassword) { toast.error("Inserisci la password attuale per cambiare username"); setSaving(false); return; }
        await axios.put(`${API}/auth/username`, { new_username: newUsername, password: currentPassword });
      }

      toast.success("Profilo aggiornato");
      setProfileOpen(false);
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const fieldRows = [
    { label: "Username", value: user?.username },
    { label: "Email", value: user?.email },
    { label: "Telefono", value: user?.telefono },
    { label: "Codice Fiscale", value: user?.codice_fiscale },
    { label: "Indirizzo", value: user?.indirizzo },
    { label: "IBAN", value: user?.iban },
  ];

  return (
    <div className="w-64 h-screen bg-stone-900 text-stone-100 flex flex-col shrink-0">
      <div className="p-6">
        <h1 className="font-heading text-xl text-white tracking-tight">La Gestione della Tua Casa</h1>
        <p className="text-[11px] text-stone-500 mt-0.5 font-mono tracking-wider uppercase">Property Manager</p>
      </div>
      <Separator className="bg-stone-800" />
      <nav className="flex-1 p-3 space-y-0.5 mt-2">
        {navItems
          .filter(item => !item.adminOnly || isAdmin)
          .map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleClick}
                data-testid={item.testId}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-all duration-150 ${
                  active ? "bg-white/10 text-white font-medium" : "text-stone-400 hover:text-stone-100 hover:bg-white/5"
                }`}
              >
                <item.icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
                {item.label}
              </Link>
            );
          })}
      </nav>
      <Separator className="bg-stone-800" />
      <div className="p-4">
        <button onClick={openProfile} data-testid="sidebar-profile-button" className="flex items-center gap-3 mb-3 w-full text-left hover:bg-white/5 rounded-sm p-1.5 -m-1.5 transition-colors group">
          <div className="h-9 w-9 rounded-sm bg-amber-600 flex items-center justify-center text-white text-sm font-semibold">
            {user?.name?.charAt(0) || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate font-medium group-hover:text-amber-200 transition-colors">{displayName}</p>
            <p className="text-[11px] text-stone-500">{isAdmin ? "Amministratore" : "Proprietario"}</p>
          </div>
          <Settings className="h-3.5 w-3.5 text-stone-600 group-hover:text-stone-400 transition-colors" />
        </button>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            data-testid="sidebar-theme-toggle"
            className="flex-1 justify-start text-stone-400 hover:text-white hover:bg-white/5 h-9"
            onClick={toggleTheme}
          >
            {dark ? <Sun className="h-4 w-4 mr-2" strokeWidth={1.5} /> : <Moon className="h-4 w-4 mr-2" strokeWidth={1.5} />}
            {dark ? "Chiaro" : "Scuro"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-testid="sidebar-logout-button"
            className="flex-1 justify-start text-stone-400 hover:text-white hover:bg-white/5 h-9"
            onClick={() => { logout(); handleClick(); }}
          >
            <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Esci
          </Button>
        </div>
      </div>

      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Il mio Profilo</DialogTitle>
            <DialogDescription className="text-sm text-stone-500">Visualizza e modifica i tuoi dati</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-stone-600">Nome</Label>
                <Input value={profileForm.name || ""} onChange={e => setProfileForm({ ...profileForm, name: e.target.value })} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Cognome</Label>
                <Input value={profileForm.cognome || ""} onChange={e => setProfileForm({ ...profileForm, cognome: e.target.value })} className="mt-1 bg-white" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-stone-600">Email</Label>
              <Input value={profileForm.email || ""} onChange={e => setProfileForm({ ...profileForm, email: e.target.value })} className="mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-xs text-stone-600">Telefono</Label>
              <Input value={profileForm.telefono || ""} onChange={e => setProfileForm({ ...profileForm, telefono: e.target.value })} className="mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-xs text-stone-600">Codice Fiscale</Label>
              <Input value={profileForm.codice_fiscale || ""} onChange={e => setProfileForm({ ...profileForm, codice_fiscale: e.target.value.toUpperCase() })} className="mt-1 bg-white font-mono" maxLength={16} />
            </div>
            <div>
              <Label className="text-xs text-stone-600">Indirizzo</Label>
              <Input value={profileForm.indirizzo || ""} onChange={e => setProfileForm({ ...profileForm, indirizzo: e.target.value })} className="mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-xs text-stone-600">IBAN</Label>
              <Input value={profileForm.iban || ""} onChange={e => setProfileForm({ ...profileForm, iban: e.target.value.toUpperCase() })} className="mt-1 bg-white font-mono" maxLength={34} />
            </div>

            <Separator />

            <div className="p-3 bg-stone-50 border border-stone-200 rounded-sm">
              <p className="text-xs font-medium text-stone-700 mb-3">Modifica Credenziali</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-stone-600">Nuovo Username</Label>
                  <Input value={newUsername} onChange={e => setNewUsername(e.target.value)} placeholder={user?.username} className="mt-1 bg-white" />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Nuova Password</Label>
                  <div className="relative mt-1">
                    <Input type={showPassword ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Lascia vuoto per non cambiare" className="bg-white pr-8" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {newUsername && newUsername !== user?.username && (
                  <div>
                    <Label className="text-xs text-stone-600">Password Attuale (richiesta per cambio username)</Label>
                    <Input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Conferma con la tua password" className="mt-1 bg-white" />
                  </div>
                )}
              </div>
            </div>

            <Button onClick={saveProfile} disabled={saving} data-testid="profile-save-button" className="w-full bg-stone-900 hover:bg-stone-800 text-white">
              {saving ? "Salvataggio..." : "Salva Modifiche"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
